import { api } from '@/lib/api-client'
import { isDemoUser } from '@/shared/constants/demo-data'

export interface FieldOfficer {
  id: string
  display_name: string
  // Nullable: field officers can be created without an email (phone-OTP login).
  email: string | null
  contact_number?: string
}

export function getFieldOfficers(): Promise<FieldOfficer[]> {
  return api
    .get<FieldOfficer[] | { users: FieldOfficer[]; total_count: number }>('/users', {
      params: { role: 'field_officer' },
    })
    .then((d) => (Array.isArray(d) ? d : d.users ?? []))
    .then((users) => users.filter((u) => !isDemoUser(u.id)))
}

// HODs use email + password login (admin-style credentials), so we surface
// email rather than phone in the picker. Same response envelope as the
// other role-filtered list endpoints.
export interface HodUser {
  id: string
  display_name: string
  email: string | null
  contact_number?: string | null
  is_active?: boolean
}

export function getHods(): Promise<HodUser[]> {
  return api
    .get<HodUser[] | { users: HodUser[]; total_count: number }>('/users', {
      params: { role: 'hod' },
    })
    .then((d) => (Array.isArray(d) ? d : d.users ?? []))
    .then((users) => users.filter((u) => !isDemoUser(u.id)))
}

// ── /users/me — self-profile, callable by any authenticated role ────────────
// Backend hydrates role-conditional fields the same way as /users/{id}:
//   role=hod       → departments_owned: present (may be [])
//   any other role → the field is absent
//
// This is the authoritative source for an HOD's owned departments — we used
// to infer the list from inbox issues, which missed any department with zero
// open issues. Replaces that workaround.
export interface MeOwnedDepartment {
  id: string
  name: string
  entity_id: string
  entity_name: string
}

export interface MeProfile {
  id: string
  user_name: string
  display_name: string
  email: string | null
  contact_number: string | null
  role_name: string
  is_active: boolean
  departments_owned?: MeOwnedDepartment[]
}

export function getMe(): Promise<MeProfile> {
  return api.get<MeProfile>('/users/me')
}

// ── PATCH /users/me — direct field updates ──────────────────────────────────
// Allowed payloads per backend (Slice A + BE-21, 2026-05-11):
//   display_name  → any role, direct save
//   contact_number → admin/super_admin/HOD only, REQUIRES current_password
//                    step-up auth (phone is a password-recovery channel since
//                    BE-21; a session-hijacker who doesn't know the password
//                    can't swap phones unilaterally)
//                    FOs must use the change-request flow instead.
// Anything else (email, password, role, user_name) is rejected here with a
// pointer to the right endpoint.
export interface PatchMeRequest {
  display_name?: string
  contact_number?: string
  // Required when contact_number is present in the body. Omitting it
  // returns 400 from backend. Wrong value returns 401.
  current_password?: string
}

export function patchMe(body: PatchMeRequest): Promise<MeProfile> {
  return api.patch<MeProfile>('/users/me', body, {
    successMessage: 'Profile updated.',
    suppressErrorToast: true,
  })
}

// ── Change requests (admin-approval workflow, Slice B) ─────────────────────

export type ChangeRequestField = 'email' | 'phone'
export type ChangeRequestStatus = 'pending' | 'approved' | 'rejected'

export interface MyChangeRequest {
  id: string
  field: ChangeRequestField
  current_value: string | null
  new_value: string
  status: ChangeRequestStatus
  requested_at: string
  decided_at: string | null
  rejection_reason: string | null
}

export interface CreateChangeRequestBody {
  field: ChangeRequestField
  new_value: string
}

export function createMyChangeRequest(
  body: CreateChangeRequestBody,
): Promise<MyChangeRequest> {
  return api.post<MyChangeRequest>('/users/me/change-requests', body, {
    successMessage: 'Change submitted for admin approval.',
    suppressErrorToast: true,
  })
}

export interface ListMyChangeRequestsParams {
  status?: ChangeRequestStatus
}

export function listMyChangeRequests(
  params?: ListMyChangeRequestsParams,
): Promise<MyChangeRequest[]> {
  return api
    .get<MyChangeRequest[] | { items: MyChangeRequest[] }>(
      '/users/me/change-requests',
      { params },
    )
    .then((d) => (Array.isArray(d) ? d : d.items ?? []))
}
